import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Modal, Form, Input, Button, Row, Col, message } from 'antd';
import { sendPostRequest, sendPutRequest } from 'redux/sagas/utils';
import { SERVER_IP } from 'assets/Config';
import { NOTIFICATION_STATUS_TYPES } from 'constants/app-constants';
import { showToast } from 'helpers';

const EditPasswordModal = ({ open, onClose, userData, refreshList }) => {
	const [form] = Form.useForm();
	const [step, setStep] = useState(1);
	const [loading, setLoading] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);
	const globalRedux = useSelector((state) => state.globalRedux);

	useEffect(() => {
		if (!open) {
			form.resetFields();
			setStep(1);
			setResendCooldown(0);
		}
	}, [open, form]);

	useEffect(() => {
		let timer;
		if (resendCooldown > 0) {
			timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
		}
		return () => clearTimeout(timer);
	}, [resendCooldown]);

	const handleSendOTP = async () => {
		try {
			setLoading(true);
			const res = await sendPostRequest({
				url: `${SERVER_IP}auth/requestotp`,
				body: {
					countryCode: '+91',
					mobile: userData?.mobile,
				},
			});
			setLoading(false);
			if (res?.data?.type === 'success') {
				message.success('OTP sent successfully');
				setStep(2);
				setResendCooldown(30);
			} else {
				showToast('Failed', res?.data?.message || 'OTP failed to send', NOTIFICATION_STATUS_TYPES.ERROR);
			}
		} catch (err) {
			setLoading(false);
			message.error('Error sending OTP');
		}
	};

	const handleVerifyAndChangePassword = async (values) => {
		const { otp, password } = values;
		try {
			setLoading(true);
			const response = await sendPostRequest({
				url: `${SERVER_IP}auth/verifyotp`,
				body: {
					countryCode: '+91',
					mobile: userData?.mobile,
					otp,
				},
			});
			if (response?.data?.type === 'success') {
				const updateRes = await sendPutRequest({
					url: `${SERVER_IP}user/${userData.userId}?orgId=${globalRedux?.selectedOrganization?._id}`,
					body: {
						password,
					},
				});
				if (updateRes?.status === 200) {
					message.success('Password changed successfully');
					refreshList?.();
					onClose();
				} else {
					message.error(updateRes?.data?.message || 'Failed to update password');
				}
			} else {
				message.error(response?.data?.message || 'OTP verification failed');
			}
		} catch (error) {
			message.error('An error occurred');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			title={`Edit Password - ${userData?.firstName} ${userData?.lastName}`}
			onCancel={onClose}
			footer={null}
			destroyOnClose>
			<Form form={form} onFinish={handleVerifyAndChangePassword} layout="vertical">
				<AnimatePresence mode="wait">
					{step === 1 && (
						<motion.div
							key="step1"
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -50 }}
							transition={{ duration: 0.3 }}>
							<Row gutter={[12, 12]}>
								<Col span={24}>
									<Button loading={loading} type="primary" onClick={handleSendOTP} block>
										Send OTP to {userData?.mobile}
									</Button>
								</Col>
							</Row>
						</motion.div>
					)}

					{step === 2 && (
						<motion.div
							key="step2"
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -50 }}
							transition={{ duration: 0.3 }}>
							<Row gutter={[12, 12]}>
								<Col span={24}>
									<Form.Item
										name="otp"
										label="Enter OTP"
										rules={[{ required: true, message: 'OTP is required' }]}>
										<Input placeholder="Enter the OTP" maxLength={6} />
									</Form.Item>
								</Col>
								<Col span={24}>
									<Form.Item
										name="password"
										label="New Password"
										rules={[{ required: true, message: 'Password is required' }]}>
										<Input.Password placeholder="Enter new password" />
									</Form.Item>
								</Col>
								<Col span={24}>
									<Button type="primary" htmlType="submit" loading={loading} block>
										Change Password
									</Button>
								</Col>
								<Col span={24} style={{ textAlign: 'center', marginTop: 8 }}>
									<Button
										type="link"
										disabled={resendCooldown > 0}
										onClick={handleSendOTP}>
										{resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
									</Button>
								</Col>
							</Row>
						</motion.div>
					)}
				</AnimatePresence>
			</Form>
		</Modal>
	);
};

export default EditPasswordModal;
